import { MessageFilled } from "@ant-design/icons";
import { Flex } from "antd/lib";
import { FC, useMemo } from "react";
import { Market, TaskStatus } from "tm-shared/types";

import { StatusCycleButton } from "@/components/CycleButton";
import { GalleryUploader } from "@/components/GalleryUploader";
import { OutlineIconButton } from "@/components/OutlineIconButton";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";
import { useMarketDistance } from "@/hooks/useMarketDistance";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { StyledBottomSheetTop } from "./Styled";

interface BottomSheetContentProps {
    title: string;
    activeStatus: TaskStatus;
    savedPhotos: string[];
    activeItemId: string;
    marketId: string;
    captionInfo: {
        market: Market;
        taskName: string;
    };
    onChangeTaskStatus: (value: TaskStatus) => void;
    onUploadPhoto: (photo: string) => void;
    onDeletePhoto: (photoId: string) => void;
    onOpenCommentModal: () => void;
}

export const BottomSheetContent: FC<BottomSheetContentProps> = ({
    title,
    activeStatus,
    savedPhotos,
    activeItemId,
    marketId,
    captionInfo,
    onChangeTaskStatus,
    onOpenCommentModal,
    onUploadPhoto,
    onDeletePhoto,
}) => {
    const { firstName, lastName } = useAppSelector((state) => state.user);

    const { market, taskName } = captionInfo;

    const { distance } = useMarketDistance({ marketGeo: market.geo });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                marketName: market.name,
                taskName,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, market.name, taskName],
    );

    return (
        <>
            <StyledBottomSheetTop>
                <PrimaryText>{title}</PrimaryText>
                <Flex gap="20px">
                    <OutlineIconButton
                        icon={<MessageFilled />}
                        onClick={() => onOpenCommentModal()}
                        disabled={activeStatus === TaskStatus.Incomplete}
                    />

                    <StatusCycleButton
                        value={activeStatus}
                        onChange={onChangeTaskStatus}
                        states={[TaskStatus.Incomplete, TaskStatus.Rejected]}
                        disabled={activeStatus === TaskStatus.Completed}
                    />
                </Flex>
            </StyledBottomSheetTop>

            <GalleryUploader
                previewTitle={title}
                onDelete={onDeletePhoto}
                onUpload={onUploadPhoto}
                activeItemId={activeItemId}
                marketId={marketId}
                savedPhotos={savedPhotos}
                disabled={activeStatus === TaskStatus.Rejected}
                captionLines={captions}
            />
        </>
    );
};
